import {useEffect, useMemo, useState} from "react";

import {type HomeMatchConfigurationSourceSection, PreferencesSurveySource} from "@pg-rp/common-types";

import {IApplicationData} from "../../application/types/IApplicationData";
import {savePreferences} from "../../auth/state/registration_flow_slice";
import {useGetClientInfoQuery} from "../../client/api/get_client_info";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../for_you/api/for_you_client";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {useGetRegionDetailListQuery} from "../../region/api/get_region_detail_list";
import {useAppDispatch} from "../../store/hooks";
import {useGetUserProfileQuery} from "../api/get_user_profile";
import {useGetHomeMatchConfigurationQuery, usePatchHomeMatchConfigurationMutation} from "../api/patch_home_match_configuration";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../constants/preferences/application_preferences_form";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource,
    HomeMatchBasicPreferencesFlowStep
} from "../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {getHomeMatchConfigurationSourceSection, type HomeMatchConfigurationSourceSectionTrigger} from "../utils/get_home_match_configuration_source_section";
import {buildHomeMatchBasicClientPayload, getHomeMatchBasicContactData, hasCompletedHomeMatchSurvey} from "../utils/preferences/home_match_basic_preferences";
import {useSurveyDefaultValues} from "./use_survey_default_values";

const HOME_MATCH_BASIC_ENABLED_NOTIFICATION = "Usługa automatycznych zapytań Homematch została włączona";

interface IUseHomeMatchBasicPreferencesFlowParams {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    defaultHomeMatchBasicConsent: boolean;
    initialStep: HomeMatchBasicPreferencesFlowInitialStep;
    propertyId?: number | null;
    offerId?: number | null;
    originalApplicationResponse?: IApplicationData | null;
    onComplete: (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => void;
}

export const useHomeMatchBasicPreferencesFlow = (params: IUseHomeMatchBasicPreferencesFlowParams) => {
    const {isOpen, source, defaultHomeMatchBasicConsent, initialStep, propertyId, offerId, originalApplicationResponse, onComplete} = params;
    const dispatch = useAppDispatch();
    const [step, setStep] = useState<HomeMatchBasicPreferencesFlowStep>("resolving");
    const [isEditingSurvey, setIsEditingSurvey] = useState(false);
    const [homeMatchBasicConsent, setHomeMatchBasicConsent] = useState(defaultHomeMatchBasicConsent);
    const [lastSavedValues, setLastSavedValues] = useState<IPreferencesDefaultValues | null>(null);
    const [hasSavedSurveyInCurrentFlow, setHasSavedSurveyInCurrentFlow] = useState(false);
    const [phoneVerificationReason, setPhoneVerificationReason] = useState<"beforePreferences" | "afterSave">("afterSave");
    const [pendingConfigurationSourceSection, setPendingConfigurationSourceSection] = useState<HomeMatchConfigurationSourceSection | null>(null);

    const {data: clientInfo, isLoading: isClientInfoLoading} = useGetClientInfoQuery();
    const {data: userProfile, isFetching: isUserProfileFetching, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const {
        data: homeMatchConfiguration,
        isFetching: isHomeMatchConfigurationFetching,
        isLoading: isHomeMatchConfigurationLoading
    } = useGetHomeMatchConfigurationQuery();
    const {defaultValues, isLoading: isDefaultValuesLoading} = useSurveyDefaultValues({
        maxBudget: PREFERENCES_FORM_MAX_DEFAULT,
        propertyId,
        offerId
    });
    const [patchHomeMatchConfiguration, {isLoading: isConfigurationUpdateLoading}] = usePatchHomeMatchConfigurationMutation();
    const [postClient, {isLoading: isPostClientLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isLoading: isUpdateClientLoading}] = useUpdateForYouClientMutation();

    const summaryValues = lastSavedValues || defaultValues;
    const regionIds = useMemo(
        () => summaryValues.regionSelector.map((region) => region.value),
        [summaryValues.regionSelector.map((region) => region.value).join(",")]
    );
    const {data: regionDetails, isLoading: isRegionLoading} = useGetRegionDetailListQuery({regionIds}, {skip: !regionIds.length});
    const surveySource = ["afterLead", "afterMultilead"].includes(source) ? PreferencesSurveySource.AFTER_LEAD : PreferencesSurveySource.PREFERENCES;
    const isHomeMatchActive = homeMatchConfiguration?.is_active === true;

    useEffect(() => {
        if (!isOpen) {
            setStep("resolving");
            setIsEditingSurvey(false);
            setHomeMatchBasicConsent(defaultHomeMatchBasicConsent);
            setLastSavedValues(null);
            setHasSavedSurveyInCurrentFlow(false);
            setPhoneVerificationReason("afterSave");
            setPendingConfigurationSourceSection(null);
            return;
        }

        if (
            step !== "resolving" ||
            isClientInfoLoading ||
            isHomeMatchConfigurationFetching ||
            isHomeMatchConfigurationLoading ||
            isUserProfileFetching ||
            isUserProfileLoading ||
            (originalApplicationResponse?.user_created && !userProfile) ||
            (userProfile && typeof userProfile.phone_verified !== "boolean")
        ) {
            return;
        }

        if (!userProfile) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        if (initialStep === "survey") {
            setStep("survey");
            return;
        }

        if (isHomeMatchActive) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        if (initialStep === "summary") {
            setStep("summary");
            return;
        }

        if (userProfile?.phone_verified === false) {
            setPhoneVerificationReason("beforePreferences");
            setStep("phoneVerification");
            return;
        }

        setStep(hasCompletedHomeMatchSurvey(clientInfo) ? "summary" : "survey");
    }, [
        clientInfo,
        defaultHomeMatchBasicConsent,
        initialStep,
        isClientInfoLoading,
        isHomeMatchActive,
        isHomeMatchConfigurationFetching,
        isHomeMatchConfigurationLoading,
        isOpen,
        isUserProfileFetching,
        isUserProfileLoading,
        onComplete,
        originalApplicationResponse?.user_created,
        step,
        userProfile
    ]);

    const saveCurrentSurvey = async () => {
        const contactData = getHomeMatchBasicContactData(userProfile, originalApplicationResponse);

        if (!contactData) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return false;
        }

        const postData = buildHomeMatchBasicClientPayload({
            values: summaryValues,
            contactData,
            maxBudget: PREFERENCES_FORM_MAX_DEFAULT,
            surveySource,
            isExplicitSubmit: true
        });

        try {
            if (clientInfo || hasSavedSurveyInCurrentFlow) {
                await updateClient(postData).unwrap();
            } else {
                await postClient(postData).unwrap();
                setHasSavedSurveyInCurrentFlow(true);
            }

            const lower = typeof summaryValues.rooms.lower === "number" ? summaryValues.rooms.lower : 1;
            const upper = typeof summaryValues.rooms.upper === "number" ? summaryValues.rooms.upper : 5;

            dispatch(
                savePreferences({
                    regions: postData.regions || [],
                    price: !summaryValues.budget || summaryValues.budget >= PREFERENCES_FORM_MAX_DEFAULT ? undefined : summaryValues.budget,
                    rooms: {lower, upper}
                })
            );

            return true;
        } catch (error) {
            notifyBugsnag(error, "HomeMatchBasicPreferencesFlow preferences save failed", {postData});
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
            return false;
        }
    };

    const saveHomeMatchConfiguration = async (consent: boolean, sourceSection: HomeMatchConfigurationSourceSection, allowPhoneVerificationFlow = true) => {
        setPendingConfigurationSourceSection(sourceSection);

        try {
            await patchHomeMatchConfiguration({
                is_active: consent,
                source_section: sourceSection
            }).unwrap();
        } catch (error) {
            if (consent && allowPhoneVerificationFlow && isHomeMatchPhoneVerificationRequiredError(error)) {
                setStep("phoneVerification");
                return false;
            }

            setPendingConfigurationSourceSection(null);
            notifyBugsnag(error, "HomeMatchBasicPreferencesFlow save failed");
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
            return false;
        }

        setPendingConfigurationSourceSection(null);

        if (consent) {
            dispatch(addSuccessNotification(HOME_MATCH_BASIC_ENABLED_NOTIFICATION));
        }

        return true;
    };

    const saveHomeMatchDecision = async (consent: boolean, trigger: HomeMatchConfigurationSourceSectionTrigger = "summaryModal") => {
        const isSurveySaved = await saveCurrentSurvey();

        if (!isSurveySaved) {
            return;
        }

        const isConfigurationSaved = await saveHomeMatchConfiguration(
            consent,
            getHomeMatchConfigurationSourceSection({
                flowSource: source,
                trigger
            })
        );

        if (isConfigurationSaved) {
            onComplete("homeMatchCompleted");
        }
    };

    const handleSurveySuccess = (values: IPreferencesDefaultValues) => {
        setLastSavedValues(values);
        setHasSavedSurveyInCurrentFlow(true);
        setIsEditingSurvey(false);
        setHomeMatchBasicConsent(defaultHomeMatchBasicConsent);

        if (isHomeMatchActive) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        setStep("summary");
    };

    const handleSummaryConfirm = () => {
        if (!homeMatchBasicConsent) {
            setStep("resignation");
            return;
        }

        saveHomeMatchDecision(true);
    };

    const handleEditPreferences = () => {
        setIsEditingSurvey(true);
        setStep("survey");
    };

    const handleSummaryClose = () => {
        setStep("resignation");
    };

    const handleSurveyCancel = () => {
        setStep("summary");
    };

    const handleResignationClose = () => {
        setStep("summary");
    };

    const handleKeepEnabled = () => {
        setHomeMatchBasicConsent(true);
        saveHomeMatchDecision(true, "stayModal");
    };

    const handlePhoneVerificationSuccess = async () => {
        if (phoneVerificationReason === "beforePreferences") {
            setStep(hasCompletedHomeMatchSurvey(clientInfo) ? "summary" : "survey");
            return;
        }

        const isConfigurationSaved = await saveHomeMatchConfiguration(
            true,
            pendingConfigurationSourceSection ??
                getHomeMatchConfigurationSourceSection({
                    flowSource: source,
                    trigger: "summaryModal"
                }),
            false
        );

        if (isConfigurationSaved) {
            onComplete("homeMatchCompleted");
        }
    };

    const handlePhoneVerificationClose = () => {
        if (phoneVerificationReason === "beforePreferences") {
            onComplete();
            return;
        }

        setStep("summary");
    };

    const handlePhoneVerificationChangeNumber = () => {
        setStep("phoneUpdate");
    };

    const handlePhoneUpdateSuccess = () => {
        setStep("phoneVerification");
    };

    const handlePhoneUpdateClose = () => {
        setStep("phoneVerification");
    };

    return {
        step,
        isEditingSurvey,
        isLoading: isClientInfoLoading || isDefaultValuesLoading || isUserProfileFetching || isUserProfileLoading || isRegionLoading,
        isHomeMatchConfigurationLoading: isHomeMatchConfigurationFetching || isHomeMatchConfigurationLoading,
        isSaving: isConfigurationUpdateLoading || isPostClientLoading || isUpdateClientLoading,
        surveySource,
        summaryValues,
        regionDetails: regionIds.length ? regionDetails : [],
        homeMatchBasicConsent,
        setHomeMatchBasicConsent,
        handleSurveySuccess,
        handleSummaryConfirm,
        handleSummaryClose,
        handleEditPreferences,
        handleSurveyCancel,
        handleResignationClose,
        handleKeepEnabled,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationClose,
        handlePhoneVerificationChangeNumber,
        handlePhoneUpdateSuccess,
        handlePhoneUpdateClose,
        saveHomeMatchDecision
    };
};

const isHomeMatchPhoneVerificationRequiredError = (error: unknown) => {
    const rtkError = error as {status?: number; data?: {is_active?: unknown}};

    if (rtkError.status !== 400 || !Array.isArray(rtkError.data?.is_active)) {
        return false;
    }

    return rtkError.data.is_active.some((message) => typeof message === "string" && message.includes("zweryfikuj numer telefonu"));
};
import {useEffect, useMemo, useState} from "react";

import {type HomeMatchConfigurationSourceSection, PreferencesSurveySource} from "@pg-rp/common-types";

import {IApplicationData} from "../../application/types/IApplicationData";
import {savePreferences} from "../../auth/state/registration_flow_slice";
import {useGetClientInfoQuery} from "../../client/api/get_client_info";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../for_you/api/for_you_client";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";
import {addWarningNotification} from "../../notifications/utils/add_warning_notification";
import {useGetRegionDetailListQuery} from "../../region/api/get_region_detail_list";
import {useAppDispatch} from "../../store/hooks";
import {useGetUserProfileQuery} from "../api/get_user_profile";
import {useGetHomeMatchConfigurationQuery, usePatchHomeMatchConfigurationMutation} from "../api/patch_home_match_configuration";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../constants/preferences/application_preferences_form";
import {
    HomeMatchBasicPreferencesFlowCompletionReason,
    HomeMatchBasicPreferencesFlowInitialStep,
    HomeMatchBasicPreferencesFlowSource,
    HomeMatchBasicPreferencesFlowStep
} from "../types/HomeMatchBasicPreferencesFlowTypes";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {getHomeMatchConfigurationSourceSection, type HomeMatchConfigurationSourceSectionTrigger} from "../utils/get_home_match_configuration_source_section";
import {buildHomeMatchBasicClientPayload, getHomeMatchBasicContactData, hasCompletedHomeMatchSurvey} from "../utils/preferences/home_match_basic_preferences";
import {useSurveyDefaultValues} from "./use_survey_default_values";

const HOME_MATCH_BASIC_ENABLED_NOTIFICATION = "Usługa automatycznych zapytań Homematch została włączona";

interface IUseHomeMatchBasicPreferencesFlowParams {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    defaultHomeMatchBasicConsent: boolean;
    initialStep: HomeMatchBasicPreferencesFlowInitialStep;
    propertyId?: number | null;
    offerId?: number | null;
    originalApplicationResponse?: IApplicationData | null;
    onComplete: (reason?: HomeMatchBasicPreferencesFlowCompletionReason) => void;
}

export const useHomeMatchBasicPreferencesFlow = (params: IUseHomeMatchBasicPreferencesFlowParams) => {
    const {isOpen, source, defaultHomeMatchBasicConsent, initialStep, propertyId, offerId, originalApplicationResponse, onComplete} = params;
    const dispatch = useAppDispatch();
    const [step, setStep] = useState<HomeMatchBasicPreferencesFlowStep>("resolving");
    const [isEditingSurvey, setIsEditingSurvey] = useState(false);
    const [homeMatchBasicConsent, setHomeMatchBasicConsent] = useState(defaultHomeMatchBasicConsent);
    const [lastSavedValues, setLastSavedValues] = useState<IPreferencesDefaultValues | null>(null);
    const [hasSavedSurveyInCurrentFlow, setHasSavedSurveyInCurrentFlow] = useState(false);
    const [phoneVerificationReason, setPhoneVerificationReason] = useState<"beforePreferences" | "afterSave">("afterSave");
    const [pendingConfigurationSourceSection, setPendingConfigurationSourceSection] = useState<HomeMatchConfigurationSourceSection | null>(null);

    const {data: clientInfo, isLoading: isClientInfoLoading} = useGetClientInfoQuery();
    const {data: userProfile, isFetching: isUserProfileFetching, isLoading: isUserProfileLoading} = useGetUserProfileQuery();
    const {
        data: homeMatchConfiguration,
        isFetching: isHomeMatchConfigurationFetching,
        isLoading: isHomeMatchConfigurationLoading
    } = useGetHomeMatchConfigurationQuery();
    const {defaultValues, isLoading: isDefaultValuesLoading} = useSurveyDefaultValues({
        maxBudget: PREFERENCES_FORM_MAX_DEFAULT,
        propertyId,
        offerId
    });
    const [patchHomeMatchConfiguration, {isLoading: isConfigurationUpdateLoading}] = usePatchHomeMatchConfigurationMutation();
    const [postClient, {isLoading: isPostClientLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isLoading: isUpdateClientLoading}] = useUpdateForYouClientMutation();

    const summaryValues = lastSavedValues || defaultValues;
    const regionIds = useMemo(
        () => summaryValues.regionSelector.map((region) => region.value),
        [summaryValues.regionSelector.map((region) => region.value).join(",")]
    );
    const {data: regionDetails, isLoading: isRegionLoading} = useGetRegionDetailListQuery({regionIds}, {skip: !regionIds.length});
    const surveySource = ["afterLead", "afterMultilead"].includes(source) ? PreferencesSurveySource.AFTER_LEAD : PreferencesSurveySource.PREFERENCES;
    const isHomeMatchActive = homeMatchConfiguration?.is_active === true;

    useEffect(() => {
        if (!isOpen) {
            setStep("resolving");
            setIsEditingSurvey(false);
            setHomeMatchBasicConsent(defaultHomeMatchBasicConsent);
            setLastSavedValues(null);
            setHasSavedSurveyInCurrentFlow(false);
            setPhoneVerificationReason("afterSave");
            setPendingConfigurationSourceSection(null);
            return;
        }

        if (
            step !== "resolving" ||
            isClientInfoLoading ||
            isHomeMatchConfigurationFetching ||
            isHomeMatchConfigurationLoading ||
            isUserProfileFetching ||
            isUserProfileLoading ||
            (originalApplicationResponse?.user_created && !userProfile) ||
            (userProfile && typeof userProfile.phone_verified !== "boolean")
        ) {
            return;
        }

        if (!userProfile) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        if (initialStep === "survey") {
            setStep("survey");
            return;
        }

        if (isHomeMatchActive) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        if (initialStep === "summary") {
            setStep("summary");
            return;
        }

        if (userProfile?.phone_verified === false) {
            setPhoneVerificationReason("beforePreferences");
            setStep("phoneVerification");
            return;
        }

        setStep(hasCompletedHomeMatchSurvey(clientInfo) ? "summary" : "survey");
    }, [
        clientInfo,
        defaultHomeMatchBasicConsent,
        initialStep,
        isClientInfoLoading,
        isHomeMatchActive,
        isHomeMatchConfigurationFetching,
        isHomeMatchConfigurationLoading,
        isOpen,
        isUserProfileFetching,
        isUserProfileLoading,
        onComplete,
        originalApplicationResponse?.user_created,
        step,
        userProfile
    ]);

    const saveCurrentSurvey = async () => {
        const contactData = getHomeMatchBasicContactData(userProfile, originalApplicationResponse);

        if (!contactData) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return false;
        }

        const postData = buildHomeMatchBasicClientPayload({
            values: summaryValues,
            contactData,
            maxBudget: PREFERENCES_FORM_MAX_DEFAULT,
            surveySource,
            isExplicitSubmit: true
        });

        try {
            if (clientInfo || hasSavedSurveyInCurrentFlow) {
                await updateClient(postData).unwrap();
            } else {
                await postClient(postData).unwrap();
                setHasSavedSurveyInCurrentFlow(true);
            }

            const lower = typeof summaryValues.rooms.lower === "number" ? summaryValues.rooms.lower : 1;
            const upper = typeof summaryValues.rooms.upper === "number" ? summaryValues.rooms.upper : 5;

            dispatch(
                savePreferences({
                    regions: postData.regions || [],
                    price: !summaryValues.budget || summaryValues.budget >= PREFERENCES_FORM_MAX_DEFAULT ? undefined : summaryValues.budget,
                    rooms: {lower, upper}
                })
            );

            return true;
        } catch (error) {
            notifyBugsnag(error, "HomeMatchBasicPreferencesFlow preferences save failed", {postData});
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
            return false;
        }
    };

    const saveHomeMatchConfiguration = async (consent: boolean, sourceSection: HomeMatchConfigurationSourceSection, allowPhoneVerificationFlow = true) => {
        setPendingConfigurationSourceSection(sourceSection);

        try {
            await patchHomeMatchConfiguration({
                is_active: consent,
                source_section: sourceSection
            }).unwrap();
        } catch (error) {
            if (consent && allowPhoneVerificationFlow && isHomeMatchPhoneVerificationRequiredError(error)) {
                setStep("phoneVerification");
                return false;
            }

            setPendingConfigurationSourceSection(null);
            notifyBugsnag(error, "HomeMatchBasicPreferencesFlow save failed");
            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
            return false;
        }

        setPendingConfigurationSourceSection(null);

        if (consent) {
            dispatch(addSuccessNotification(HOME_MATCH_BASIC_ENABLED_NOTIFICATION));
        }

        return true;
    };

    const saveHomeMatchDecision = async (consent: boolean, trigger: HomeMatchConfigurationSourceSectionTrigger = "summaryModal") => {
        const isSurveySaved = await saveCurrentSurvey();

        if (!isSurveySaved) {
            return;
        }

        const isConfigurationSaved = await saveHomeMatchConfiguration(
            consent,
            getHomeMatchConfigurationSourceSection({
                flowSource: source,
                trigger
            })
        );

        if (isConfigurationSaved) {
            onComplete("homeMatchCompleted");
        }
    };

    const handleSurveySuccess = (values: IPreferencesDefaultValues) => {
        setLastSavedValues(values);
        setHasSavedSurveyInCurrentFlow(true);
        setIsEditingSurvey(false);
        setHomeMatchBasicConsent(defaultHomeMatchBasicConsent);

        if (isHomeMatchActive) {
            setStep("completed");
            onComplete("homeMatchSkipped");
            return;
        }

        setStep("summary");
    };

    const handleSummaryConfirm = () => {
        if (!homeMatchBasicConsent) {
            setStep("resignation");
            return;
        }

        saveHomeMatchDecision(true);
    };

    const handleEditPreferences = () => {
        setIsEditingSurvey(true);
        setStep("survey");
    };

    const handleSummaryClose = () => {
        setStep("resignation");
    };

    const handleSurveyCancel = () => {
        setStep("summary");
    };

    const handleResignationClose = () => {
        setStep("summary");
    };

    const handleKeepEnabled = () => {
        setHomeMatchBasicConsent(true);
        saveHomeMatchDecision(true, "stayModal");
    };

    const handlePhoneVerificationSuccess = async () => {
        if (phoneVerificationReason === "beforePreferences") {
            setStep(hasCompletedHomeMatchSurvey(clientInfo) ? "summary" : "survey");
            return;
        }

        const isConfigurationSaved = await saveHomeMatchConfiguration(
            true,
            pendingConfigurationSourceSection ??
                getHomeMatchConfigurationSourceSection({
                    flowSource: source,
                    trigger: "summaryModal"
                }),
            false
        );

        if (isConfigurationSaved) {
            onComplete("homeMatchCompleted");
        }
    };

    const handlePhoneVerificationClose = () => {
        if (phoneVerificationReason === "beforePreferences") {
            onComplete();
            return;
        }

        setStep("summary");
    };

    const handlePhoneVerificationChangeNumber = () => {
        setStep("phoneUpdate");
    };

    const handlePhoneUpdateSuccess = () => {
        setStep("phoneVerification");
    };

    const handlePhoneUpdateClose = () => {
        setStep("phoneVerification");
    };

    return {
        step,
        isEditingSurvey,
        isLoading: isClientInfoLoading || isDefaultValuesLoading || isUserProfileFetching || isUserProfileLoading || isRegionLoading,
        isHomeMatchConfigurationLoading: isHomeMatchConfigurationFetching || isHomeMatchConfigurationLoading,
        isSaving: isConfigurationUpdateLoading || isPostClientLoading || isUpdateClientLoading,
        surveySource,
        summaryValues,
        regionDetails: regionIds.length ? regionDetails : [],
        homeMatchBasicConsent,
        setHomeMatchBasicConsent,
        handleSurveySuccess,
        handleSummaryConfirm,
        handleSummaryClose,
        handleEditPreferences,
        handleSurveyCancel,
        handleResignationClose,
        handleKeepEnabled,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationClose,
        handlePhoneVerificationChangeNumber,
        handlePhoneUpdateSuccess,
        handlePhoneUpdateClose,
        saveHomeMatchDecision
    };
};

const isHomeMatchPhoneVerificationRequiredError = (error: unknown) => {
    const rtkError = error as {status?: number; data?: {is_active?: unknown}};

    if (rtkError.status !== 400 || !Array.isArray(rtkError.data?.is_active)) {
        return false;
    }

    return rtkError.data.is_active.some((message) => typeof message === "string" && message.includes("zweryfikuj numer telefonu"));
};
